'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import { trackEvent } from '@/lib/analytics'
import { buildGaugeConfigs, type GaugeConfig, type GaugeStatus } from '@/lib/soil-test/gauge-config'
import type {
  AnalysisContext,
  Citation,
  RecommendedAction,
  SoilTestInterpretation,
  SoilTestValues,
} from '@/lib/soil-test/types'
import { GRASS_AGRONOMY } from '@/lib/soil-test/agronomy-tables'
import { dedupeProducts, sumPrices } from '@/lib/soil-test/product-picks'
import { splitLede } from '@/lib/text/lede'
import { ProductPickCard } from '@/components/tools/shared/product-pick-card'
import { AffiliateDisclosure } from '@/components/tools/shared/affiliate-disclosure'

// ============================================================================
// Props
// ============================================================================

interface SoilTestResultsProps {
  values: SoilTestValues
  interpretation: SoilTestInterpretation
  analysisContext: AnalysisContext
  soilTestId: string
  claimToken: string | null
  isAuthenticated: boolean
  isPremium: boolean
  /** Called when the user wants to clear results and analyze another. */
  onReset?: () => void
  /**
   * Surfaces the free "Send me lawn reminders" path inside the Save card.
   * Wired by the page-content host to `openNewsletterModal` from
   * `useNewsletterPrompt`. Omitted for authed users (the hook short-circuits).
   */
  onEmailCapture?: () => void
}

const RETEST_THRESHOLD_DAYS = 730 // ~24 months

type PortraitStatus = 'healthy' | 'needs-work' | 'critical'

// ============================================================================
// Inline SVG character — matches the design bundle
// ============================================================================

function SoilPortraitSVG({ status, size = 88 }: { status: PortraitStatus; size?: number }) {
  const palette = {
    healthy: { bg: '#f0fdf4', ring: '#bbf7d0', grassA: '#16a34a', grassB: '#22c55e', grassC: '#15803d', soil1: '#a07a5d', soil2: '#7c5a3f', dots: '#4b3320', mark: '#fde68a' },
    'needs-work': { bg: '#f0fdf4', ring: '#bbf7d0', grassA: '#16a34a', grassB: '#facc15', grassC: '#15803d', soil1: '#a07a5d', soil2: '#7c5a3f', dots: '#4b3320', mark: '#fde68a' },
    critical: { bg: '#fffbeb', ring: '#fde68a', grassA: '#ca8a04', grassB: '#facc15', grassC: '#a16207', soil1: '#92745a', soil2: '#6b4f3a', dots: '#3f2917', mark: '#fca5a5' },
  }
  const p = palette[status]
  const blades = [
    { x: 14, h: 24, c: p.grassA, lean: -2 },
    { x: 22, h: 18, c: p.grassB, lean: 1 },
    { x: 30, h: 26, c: p.grassC, lean: -1 },
    { x: 40, h: 22, c: p.grassA, lean: 1 },
    { x: 48, h: 20, c: p.grassB, lean: -1 },
    { x: 56, h: 26, c: p.grassC, lean: 2 },
    { x: 64, h: 18, c: p.grassA, lean: -2 },
  ]
  return (
    <svg width={size} height={size * 1.25} viewBox="0 0 80 100" fill="none" aria-hidden>
      <rect x="1" y="1" width="78" height="98" rx="14" fill={p.bg} stroke={p.ring} strokeWidth="1.5" />
      {status === 'healthy' && (
        <>
          <circle cx="62" cy="16" r="5" fill={p.mark} />
          <line x1="56" y1="10" x2="54" y2="8" stroke={p.mark} strokeWidth="1.3" strokeLinecap="round" />
          <line x1="68" y1="10" x2="70" y2="8" stroke={p.mark} strokeWidth="1.3" strokeLinecap="round" />
          <line x1="62" y1="6" x2="62" y2="4" stroke={p.mark} strokeWidth="1.3" strokeLinecap="round" />
        </>
      )}
      {status === 'critical' && (
        <g>
          <path d="M 14 18 q 5 -6 10 0 q 5 -6 10 0" stroke={p.ring} strokeWidth="1.4" fill="none" strokeLinecap="round" />
          <path d="M 46 18 q 5 -6 10 0 q 5 -6 10 0" stroke={p.ring} strokeWidth="1.4" fill="none" strokeLinecap="round" />
        </g>
      )}
      {status === 'needs-work' && (
        <g opacity="0.6">
          <line x1="10" y1="14" x2="20" y2="14" stroke={p.ring} strokeWidth="1.3" strokeLinecap="round" />
          <line x1="50" y1="20" x2="58" y2="20" stroke={p.ring} strokeWidth="1.3" strokeLinecap="round" />
        </g>
      )}
      <rect x="4" y="48" width="72" height="20" fill={p.soil1} />
      <rect x="4" y="68" width="72" height="28" fill={p.soil2} />
      {[[14, 56, 1.5], [24, 62, 1.2], [40, 58, 1.4], [55, 64, 1.2], [68, 55, 1.4], [20, 80, 1.4], [48, 82, 1.6], [62, 78, 1.2], [30, 90, 1.4], [65, 92, 1.2]].map(([cx, cy, r], i) => (
        <circle key={i} cx={cx} cy={cy} r={r} fill={p.dots} opacity={0.85} />
      ))}
      <line x1="4" y1="48" x2="76" y2="48" stroke={p.dots} strokeWidth="0.7" opacity="0.4" />
      {blades.map((b, i) => (
        <path key={i}
          d={`M ${b.x} 48 Q ${b.x + b.lean} ${48 - b.h / 2} ${b.x + b.lean * 2} ${48 - b.h}`}
          stroke={b.c} strokeWidth="2" fill="none" strokeLinecap="round" />
      ))}
    </svg>
  )
}

function TestVialSVG({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size * 1.2} viewBox="0 0 28 34" fill="none" aria-hidden>
      <path d="M 9 4 V 20 L 5.5 27 a 5 5 0 0 0 4.5 5.5 h 8 a 5 5 0 0 0 4.5 -5.5 L 19 20 V 4 Z"
        fill="#f9fafb" stroke="#15803d" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M 7.3 23 L 20.7 23 L 22.5 27 a 4 4 0 0 1 -3.6 5.5 h -7.8 a 4 4 0 0 1 -3.6 -5.5 Z"
        fill="#bbf7d0" stroke="#16a34a" strokeWidth="1" />
      <circle cx="12" cy="28" r="0.9" fill="#16a34a" opacity="0.7" />
      <circle cx="16" cy="26" r="0.6" fill="#16a34a" opacity="0.55" />
      <circle cx="15" cy="30" r="0.5" fill="#16a34a" opacity="0.5" />
      <line x1="9" y1="10" x2="11" y2="10" stroke="#15803d" strokeWidth="0.8" strokeLinecap="round" />
      <line x1="9" y1="14" x2="11" y2="14" stroke="#15803d" strokeWidth="0.8" strokeLinecap="round" />
      <line x1="9" y1="18" x2="11" y2="18" stroke="#15803d" strokeWidth="0.8" strokeLinecap="round" />
      <rect x="7.5" y="2" width="13" height="4" rx="1.3" fill="#15803d" />
      <rect x="8.5" y="1" width="11" height="2" rx="1" fill="#22c55e" />
    </svg>
  )
}

function GrassRow({ width = 220, height = 20 }: { width?: number; height?: number }) {
  const count = Math.max(8, Math.round(width / 22))
  const colors = ['#16a34a', '#22c55e', '#15803d', '#86efac']
  const blades = Array.from({ length: count }, (_, i) => {
    const x = (i / (count - 1)) * (width - 8) + 4
    const h = 8 + ((i * 7) % 9)
    const lean = (i % 2 ? 1 : -1) * (1 + (i % 3))
    return { x, h, lean, c: colors[i % colors.length] }
  })
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} fill="none" aria-hidden>
      {blades.map((b, i) => (
        <path key={i}
          d={`M ${b.x} ${height - 2} Q ${b.x + b.lean} ${height - 2 - b.h / 2} ${b.x + b.lean * 2} ${height - 2 - b.h}`}
          stroke={b.c} strokeWidth="1.6" strokeLinecap="round" fill="none" />
      ))}
      <line x1="0" y1={height - 2} x2={width} y2={height - 2} stroke="#dcfce7" strokeWidth="1.2" />
    </svg>
  )
}

// ============================================================================
// Status helpers
// ============================================================================

const STATUS_STYLES: Record<GaugeStatus, { tone: string; tint: string; ring: string; ink: string; label: string }> = {
  ok: { tone: '#16a34a', tint: '#f0fdf4', ring: '#dcfce7', ink: '#15803d', label: 'In range' },
  low: { tone: '#f59e0b', tint: '#fffbeb', ring: '#fef3c7', ink: '#d97706', label: 'Low' },
  far_low: { tone: '#dc2626', tint: '#fef2f2', ring: '#fee2e2', ink: '#dc2626', label: 'Very low' },
  high: { tone: '#f59e0b', tint: '#fffbeb', ring: '#fef3c7', ink: '#d97706', label: 'High' },
  info: { tone: '#6b7280', tint: '#f9fafb', ring: '#e5e7eb', ink: '#4b5563', label: 'Info' },
}

function StatusPill({ status, children, size = 'sm' }: { status: GaugeStatus; children?: React.ReactNode; size?: 'sm' | 'lg' }) {
  const s = STATUS_STYLES[status]
  const pad = size === 'lg' ? 'px-3 py-[5px]' : 'px-[9px] py-[3px]'
  const text = size === 'lg' ? 'text-[12.5px]' : 'text-[11.5px]'
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full font-bold tracking-[0.02em] ${pad} ${text}`}
      style={{ background: s.tint, color: s.ink, border: `1px solid ${s.ring}` }}
    >
      <span className={size === 'lg' ? 'h-1.5 w-1.5 rounded-full' : 'h-[5px] w-[5px] rounded-full'} style={{ background: s.tone }} />
      {children ?? s.label}
    </span>
  )
}

type PriorityLevel = 'medium' | 'low' | 'info'

const PRIORITY_STYLES: Record<PriorityLevel, { bg: string; fg: string; ring: string; dot: string; label: string }> = {
  medium: { bg: '#fffbeb', fg: '#d97706', ring: '#fef3c7', dot: '#f59e0b', label: 'Medium priority' },
  low: { bg: '#f0fdf4', fg: '#15803d', ring: '#dcfce7', dot: '#16a34a', label: 'Low priority' },
  info: { bg: '#f9fafb', fg: '#4b5563', ring: '#e5e7eb', dot: '#9ca3af', label: 'Info only' },
}

function PriorityPill({ level }: { level: PriorityLevel }) {
  const m = PRIORITY_STYLES[level]
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-[11px] py-[5px] text-[12px] font-bold tracking-[0.02em]"
      style={{ background: m.bg, color: m.fg, border: `1px solid ${m.ring}` }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: m.dot }} />
      {m.label}
    </span>
  )
}

// ============================================================================
// Icon glyphs
// ============================================================================

function IconAlert({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  )
}

function IconInfo({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  )
}

function IconCheck({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  )
}

function IconBeaker({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M9 3h6v5l4.5 9a2 2 0 0 1-1.8 3H6.3a2 2 0 0 1-1.8-3L9 8V3z" />
      <path d="M7 13h10" />
    </svg>
  )
}

function IconDrop({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
    </svg>
  )
}

function IconCal({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  )
}

function IconBag({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <path d="M16 10a4 4 0 0 1-8 0" />
    </svg>
  )
}

function IconArrow({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden>
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  )
}

function IconSparkle({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 3l1.6 4.6L18 9l-4.4 1.4L12 15l-1.6-4.6L6 9l4.4-1.4z" />
      <path d="M19 14l.8 2.2L22 17l-2.2.8L19 20l-.8-2.2L16 17l2.2-.8z" />
    </svg>
  )
}

function IconLock({ size = 12 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  )
}

// ============================================================================
// Verdict derivation
// ============================================================================

interface VerdictSpec {
  portrait: PortraitStatus
  priorityLevel: PriorityLevel
  headline: string
  accent: string
  trailingClause: string
  subtitle: string
}

function buildVerdict(interpretation: SoilTestInterpretation, actionableCount: number, context: AnalysisContext, contextCount: number, microCount: number): VerdictSpec {
  if (interpretation.degraded) {
    return {
      portrait: 'needs-work',
      priorityLevel: 'medium',
      headline: 'Some values',
      accent: 'need a second look',
      trailingClause: 'before you act on them.',
      subtitle: interpretation.degradedReason ?? interpretation.summary,
    }
  }
  if (actionableCount === 0) {
    return {
      portrait: 'healthy',
      priorityLevel: 'low',
      headline: 'Your soil is',
      accent: 'in great shape',
      trailingClause: 'and needs only upkeep.',
      subtitle: interpretation.summary,
    }
  }
  if (actionableCount === 1) {
    return {
      portrait: 'needs-work',
      priorityLevel: 'medium',
      headline: 'Your soil is',
      accent: 'mostly healthy',
      trailingClause: 'with one fix to make.',
      subtitle: interpretation.summary,
    }
  }
  if (actionableCount === 2) {
    return {
      portrait: 'needs-work',
      priorityLevel: 'medium',
      headline: 'Your soil has',
      accent: 'two things',
      trailingClause: 'worth addressing this year.',
      subtitle: interpretation.summary,
    }
  }
  return {
    portrait: 'critical',
    priorityLevel: 'medium',
    headline: 'Your soil',
    accent: 'needs attention',
    trailingClause: 'in several places.',
    subtitle: interpretation.summary,
  }
}

function actionPriorityToLevel(p: RecommendedAction['priority']): PriorityLevel {
  if (p === 'high' || p === 'medium') return 'medium'
  if (p === 'low') return 'low'
  return 'info'
}

// ============================================================================
// Formatting helpers
// ============================================================================

function fmtLawnSize(sqft: number | null): string | null {
  if (!sqft) return null
  const acres = sqft / 43_560
  const lbl = sqft.toLocaleString('en-US') + ' sqft'
  if (acres < 0.1) return lbl
  return `${lbl} (≈ ${acres.toFixed(2)} ac)`
}

function fmtTestDate(iso: string | null): { display: string; ageDays: number } | null {
  if (!iso) return null
  const t = Date.parse(iso)
  if (isNaN(t)) return null
  const ageDays = (Date.now() - t) / (1000 * 60 * 60 * 24)
  const d = new Date(t)
  const display = d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })
  return { display, ageDays }
}

// ============================================================================
// Main component
// ============================================================================

export function SoilTestResults({
  values,
  interpretation,
  analysisContext,
  soilTestId,
  claimToken,
  isAuthenticated,
  isPremium,
  onReset,
  onEmailCapture,
}: SoilTestResultsProps) {
  const [saved, setSaved] = useState(false)
  const [savingError, setSavingError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  // ---- Slice the engine output into the three render buckets ---------------
  const { actionable, contextCards, microActions } = useMemo(() => {
    const a: RecommendedAction[] = []
    const c: RecommendedAction[] = []
    const m: RecommendedAction[] = []
    for (const act of interpretation.actions) {
      if (act.category === 'micronutrient') {
        m.push(act)
        continue
      }
      if (act.priority === 'informational') {
        // Context cards: Ca/Mg + sulfur. CEC context is shown inside the gauge
        // section itself so it doesn't compete with the Ca/Mg + S cards.
        if (act.category === 'calcium_magnesium' || act.category === 'sulfur') c.push(act)
        continue
      }
      // Actionable: high / medium / low across all non-micronutrient categories
      a.push(act)
    }
    return { actionable: a, contextCards: c, microActions: m }
  }, [interpretation.actions])

  // ---- Funnel anchor: fire once per soil_test_id on first mount ------------
  const viewedRef = useRef<string | null>(null)
  useEffect(() => {
    if (viewedRef.current === soilTestId) return
    viewedRef.current = soilTestId
    trackEvent('soil_test_results_viewed', {
      soil_test_id: soilTestId,
      actionable_count: actionable.length,
      context_count: contextCards.length,
      micro_count: microActions.length,
      has_lawn_size: analysisContext.lawnSize !== null,
      grass_type: analysisContext.grassType,
      region: analysisContext.region ?? undefined,
      degraded: interpretation.degraded,
    })
  }, [soilTestId, actionable.length, contextCards.length, microActions.length, analysisContext.lawnSize, analysisContext.grassType, analysisContext.region, interpretation.degraded])

  const cecAction = useMemo(
    () => interpretation.actions.find((x) => x.category === 'cec_context') ?? null,
    [interpretation.actions],
  )

  const verdict = useMemo(
    () => buildVerdict(interpretation, actionable.length, analysisContext, contextCards.length, microActions.length),
    [interpretation, actionable.length, analysisContext, contextCards.length, microActions.length],
  )

  const gauges = useMemo(
    () => buildGaugeConfigs(values, analysisContext.grassType),
    [values, analysisContext.grassType],
  )

  const testDate = fmtTestDate(values.testDate)
  const retestNeeded = testDate !== null && testDate.ageDays >= RETEST_THRESHOLD_DAYS
  const grassDisplay = GRASS_AGRONOMY[analysisContext.grassType]?.displayName ?? analysisContext.grassType
  const lawnSizeFmt = fmtLawnSize(analysisContext.lawnSize)

  // ---- Save flow (unchanged from prior implementation) ---------------------
  const handleSaveAttempt = async () => {
    trackEvent('soil_test_save_attempted', {
      soil_test_id: soilTestId,
      is_authenticated: isAuthenticated,
      is_premium: isPremium,
    })
    if (!isAuthenticated || !claimToken) {
      return
    }
    setSavingError(null)
    setSaving(true)
    try {
      const res = await fetch('/api/user/save-soil-test', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ claimToken }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        setSavingError(body.error || 'Could not save')
        return
      }
      setSaved(true)
    } catch {
      setSavingError('Network error. Try again.')
    } finally {
      setSaving(false)
    }
  }

  // ==========================================================================
  return (
    <div className="text-gray-900">
      {/* =====================================================================
          VERDICT HERO — full-width on a warm gradient. Stacks portrait + copy
          on mobile, side-by-side at sm+, with the report-meta aside revealing
          at lg+.
         ===================================================================== */}
      <section
        className="-mx-4 sm:-mx-6 lg:-mx-7 px-4 sm:px-6 lg:px-7 pt-6 pb-7 sm:pt-8 sm:pb-8"
        style={{ background: 'linear-gradient(180deg, #f0fdf4 0%, #ffffff 280px)' }}
      >
        <div className="max-w-[1080px] mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-start lg:gap-6">
            <div className="flex items-start gap-4 sm:gap-6 flex-1 min-w-0">
              <div className="flex-shrink-0 pt-1">
                <div className="sm:hidden">
                  <SoilPortraitSVG status={verdict.portrait} size={64} />
                </div>
                <div className="hidden sm:block">
                  <SoilPortraitSVG status={verdict.portrait} size={96} />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2 mb-3">
                  <PriorityPill level={verdict.priorityLevel} />
                  <span className="text-[12.5px] text-gray-500">
                    {actionable.length} action{actionable.length === 1 ? '' : 's'}
                    {contextCards.length > 0 && (
                      <>
                        <span className="text-gray-300 mx-1.5">·</span>
                        {contextCards.length} context note{contextCards.length === 1 ? '' : 's'}
                      </>
                    )}
                    {microActions.length > 0 && (
                      <>
                        <span className="text-gray-300 mx-1.5">·</span>
                        {microActions.length} micronutrient{microActions.length === 1 ? '' : 's'} (info)
                      </>
                    )}
                  </span>
                </div>
                <h1 className="m-0 text-[clamp(1.625rem,3.6vw,2.75rem)] leading-[1.1] font-extrabold tracking-[-0.03em] text-balance">
                  {verdict.headline}{' '}
                  <span className="text-green-600 border-b-[3px] sm:border-b-4 border-green-600 pb-[2px] sm:pb-[3px]">
                    {verdict.accent}
                  </span>{' '}
                  {verdict.trailingClause}
                </h1>
                <p className="mt-3 sm:mt-4 text-[15px] sm:text-[16px] leading-[1.55] text-gray-600 max-w-[680px]">
                  {verdict.subtitle}
                  {actionable.length > 0 && actionable[0].recommendation && (
                    <>
                      {' '}
                      <span className="text-gray-800 font-medium">{actionable[0].recommendation}</span>
                    </>
                  )}
                </p>
                {actionable.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      const isDesktop = typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches
                      const targetId = isDesktop ? 'actions-desktop' : 'actions-mobile'
                      const el = typeof document !== 'undefined' ? document.getElementById(targetId) : null
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }}
                    className="inline-flex items-center gap-1.5 mt-4 text-[14px] font-semibold text-green-700 hover:text-green-800 transition-colors"
                  >
                    See your {actionable.length} action{actionable.length === 1 ? '' : 's'}
                    <IconArrow size={12} />
                  </button>
                )}
              </div>
            </div>

            {/* Right meta aside — desktop only; mobile/tablet get a compact
                inline strip below the hero copy. */}
            <aside className="hidden lg:block flex-shrink-0 w-[280px] rounded-2xl border border-gray-200 bg-white p-4 shadow-elevation-2">
              <div className="text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500 mb-2">This report</div>
              <div className="flex items-center gap-2.5 mb-3">
                <span className="flex-shrink-0"><TestVialSVG size={28} /></span>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-semibold text-gray-900 truncate">{labLabel(values.lab)}</div>
                  <div className="text-[11.5px] text-gray-500 capitalize">{values.parsingConfidence} confidence</div>
                </div>
              </div>
              <div className="grid gap-1.5 text-[12.5px] text-gray-700">
                <Meta label="Grass type" value={grassDisplay} />
                {lawnSizeFmt && <Meta label="Lawn size" value={lawnSizeFmt} />}
                {analysisContext.zipCode && (
                  <Meta
                    label="ZIP / region"
                    value={analysisContext.region ? `${analysisContext.zipCode} · ${analysisContext.region}` : analysisContext.zipCode}
                  />
                )}
                {testDate && (
                  <Meta
                    label="Test dated"
                    value={`${testDate.display}${retestNeeded ? ' · over 2 years old' : ''}`}
                  />
                )}
              </div>
            </aside>
          </div>

          {/* Compact inline meta strip for mobile + tablet (hidden at lg+).
              Trimmed to grass + lawn size + test date — ZIP and lab name are
              available in the form context above the fold. */}
          <div className="lg:hidden mt-4 rounded-xl border border-gray-200 bg-white p-3 flex items-center gap-2.5">
            <span className="flex-shrink-0"><TestVialSVG size={24} /></span>
            <div className="min-w-0 flex-1 text-[12.5px] text-gray-700 truncate">
              <span className="font-semibold text-gray-900">{grassDisplay}</span>
              {lawnSizeFmt && <span className="text-gray-500"> · {lawnSizeFmt}</span>}
              {testDate && <span className="text-gray-500"> · {testDate.display}</span>}
            </div>
          </div>
        </div>
      </section>

      {/* =====================================================================
          RE-TEST BANNER — promoted above the body so the recommendation
          conversation starts with the right caveat.
         ===================================================================== */}
      {retestNeeded && (
        <div className="max-w-[1080px] mx-auto mt-6">
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 flex flex-col sm:flex-row sm:items-start gap-3">
            <span className="flex-shrink-0 text-amber-600 mt-0.5"><IconAlert size={18} /></span>
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-bold text-amber-700">This test is over 2 years old</div>
              <div className="text-[13px] text-gray-700 mt-1 leading-[1.5]">
                Soil chemistry shifts over time. Consider ordering a fresh test before applying significant amendments.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =====================================================================
          BODY — two-column at lg+, single column otherwise. On mobile, the
          action cards lead so the "what to do" answer comes before the data
          (matches design intent).
         ===================================================================== */}
      <div className="max-w-[1080px] mx-auto mt-6 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6 lg:gap-7">
        {/* ===== LEFT MAIN ===== */}
        <div className="min-w-0 grid gap-5 lg:gap-5">
          {/* On mobile only: action cards before gauges */}
          {actionable.length > 0 && (
            <section id="actions-mobile" className="lg:hidden scroll-mt-24" aria-label="Recommended actions (mobile)">
              <SectionHead eyebrow="What to do" title="Recommended actions" meta={actionable.length === 1 ? '1 action' : `${actionable.length} actions, ordered by priority`} />
              <div className="grid gap-4">
                {actionable.map((a, i) => (
                  <ActionCard key={a.id} action={a} index={i + 1} lawnSize={analysisContext.lawnSize} soilTestId={soilTestId} />
                ))}
              </div>
            </section>
          )}

          {/* Nutrient overview — gauge bars */}
          {gauges.length > 0 && (
            <section className="rounded-2xl border border-gray-200 bg-white shadow-elevation-2 overflow-hidden">
              <div className="px-5 sm:px-6 py-4 flex items-baseline justify-between gap-3 border-b border-gray-100">
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-green-700">Parsed values</div>
                  <h2 className="m-0 mt-1 text-[18px] sm:text-[20px] font-bold tracking-[-0.02em] text-gray-900">Nutrient overview</h2>
                </div>
                {values.warnings.length > 0 && (
                  <a href="#raw-values" className="text-[12.5px] sm:text-[13px] font-semibold text-green-700 no-underline whitespace-nowrap">
                    Warnings ({values.warnings.length}) →
                  </a>
                )}
              </div>
              {gauges.map((g) => (
                <NutrientGauge key={g.name} {...g} />
              ))}
              {cecAction && cecAction.explanation && (
                <div className="px-5 sm:px-6 py-4 border-t border-gray-100 bg-gray-50/40">
                  <div className="flex items-start gap-2.5">
                    <span className="flex-shrink-0 mt-0.5 text-gray-500"><IconInfo size={14} /></span>
                    <div className="text-[13px] text-gray-700 leading-[1.55]">
                      <strong className="text-gray-900 font-semibold">{cecAction.title}.</strong>{' '}
                      {cecAction.explanation}
                    </div>
                  </div>
                </div>
              )}
              {values.warnings.length > 0 && (
                <details id="raw-values" className="px-5 sm:px-6 py-3 border-t border-gray-100 text-[12.5px]">
                  <summary className="cursor-pointer text-gray-500 font-medium">Extraction warnings</summary>
                  <ul className="mt-2 space-y-1 pl-4 list-disc text-gray-600">
                    {values.warnings.map((w, i) => (
                      <li key={i}><strong className="font-semibold text-gray-900">{w.field}:</strong> {w.detail}</li>
                    ))}
                  </ul>
                </details>
              )}
            </section>
          )}

          {/* Recommended actions — desktop placement (hidden on mobile where it's already above) */}
          {actionable.length > 0 && (
            <section id="actions-desktop" className="hidden lg:block scroll-mt-24">
              <SectionHead
                eyebrow="What to do"
                title="Recommended actions"
                meta={`${actionable.length === 1 ? '1 action' : `${actionable.length} actions`} · sized to ${lawnSizeFmt ?? 'your lawn'}`}
                withFlourish
              />
              <div className="grid gap-4">
                {actionable.map((a, i) => (
                  <ActionCard key={a.id} action={a} index={i + 1} lawnSize={analysisContext.lawnSize} soilTestId={soilTestId} />
                ))}
              </div>
            </section>
          )}

          {/* All clear — no actionable items, surface a maintenance card */}
          {actionable.length === 0 && !interpretation.degraded && (
            <AllClearCard contextCount={contextCards.length} />
          )}

          {/* Shopping list — every recommended product across every action,
              deduped and totaled. Only renders when there are 2+ unique picks
              (a single product is already well-surfaced inline). */}
          {actionable.length > 0 && (
            <ShoppingList actionable={actionable} soilTestId={soilTestId} />
          )}

          {/* Cross-tool nudges — turn a one-time soil test into a multi-tool
              session. Only includes tools with verified URL-prefill contracts. */}
          {actionable.length > 0 && (
            <CrossToolNudges
              actionable={actionable}
              grassDisplay={grassDisplay}
              lawnSize={analysisContext.lawnSize}
              zipCode={analysisContext.zipCode}
              soilTestId={soilTestId}
            />
          )}

          {/* Context, not actions */}
          {(contextCards.length > 0 || microActions.length > 0) && (
            <section>
              <header className="mb-3.5">
                <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-gray-500">Good to know</div>
                <h2 className="m-0 mt-1 text-[18px] sm:text-[20px] font-bold tracking-[-0.02em] text-gray-900">Context, not actions</h2>
              </header>
              {contextCards.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  {contextCards.map((c) => (
                    <ContextCard key={c.id} action={c} />
                  ))}
                </div>
              )}
              {microActions.length > 0 && (
                <MicroBundle values={values} actions={microActions} />
              )}
            </section>
          )}
        </div>

        {/* ===== RIGHT STICKY RAIL (lg+ only) ===== */}
        <aside className="hidden lg:grid gap-4 content-start">
          <div className="sticky top-[84px] grid gap-4">
            <SaveCard
              saved={saved}
              saving={saving}
              savingError={savingError}
              isAuthenticated={isAuthenticated}
              isPremium={isPremium}
              onSave={handleSaveAttempt}
              onEmailCapture={onEmailCapture}
            />
            <TrustNote />
          </div>
        </aside>
      </div>

      {/* Save CTA on mobile / tablet (the right rail is hidden under lg) */}
      <div className="lg:hidden max-w-[1080px] mx-auto mt-5">
        <SaveCard
          saved={saved}
          saving={saving}
          savingError={savingError}
          isAuthenticated={isAuthenticated}
          isPremium={isPremium}
          onSave={handleSaveAttempt}
        />
      </div>

      {/* =====================================================================
          FOOTER BAND — "Analyze another soil test"
         ===================================================================== */}
      <div className="-mx-4 sm:-mx-6 lg:-mx-7 mt-8 sm:mt-10 border-t border-gray-200 bg-gray-50 px-4 sm:px-6 lg:px-7 py-6 sm:py-7">
        <div className="max-w-[1080px] mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div>
            <div className="text-[16px] sm:text-[17px] font-bold text-gray-900">Analyze another soil test</div>
            <div className="text-[13px] sm:text-[13.5px] text-gray-500 mt-1">Works for any US lawn-test lab.</div>
          </div>
          {onReset && (
            <button
              type="button"
              onClick={onReset}
              className="press inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-gray-900 px-4 sm:px-5 py-3 text-[14px] font-bold text-white hover:bg-gray-800 transition-colors whitespace-nowrap"
            >
              Upload another test
              <IconArrow size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Subcomponents
// ============================================================================

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-[12.5px]">
      <span className="text-gray-500 font-medium">{label}</span>
      <span className="text-gray-900 font-semibold text-right">{value}</span>
    </div>
  )
}

function SectionHead({ eyebrow, title, meta, withFlourish }: { eyebrow?: string; title: string; meta?: string; withFlourish?: boolean }) {
  return (
    <header className="mb-3.5">
      {withFlourish && (
        <div className="mb-2 hidden sm:block">
          <GrassRow width={220} height={20} />
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 sm:gap-3">
        <div>
          {eyebrow && <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-green-700">{eyebrow}</div>}
          <h2 className="m-0 mt-1 text-[20px] sm:text-[22px] font-extrabold tracking-[-0.02em] text-gray-900">{title}</h2>
        </div>
        {meta && <span className="text-[13px] text-gray-500">{meta}</span>}
      </div>
    </header>
  )
}

function NutrientGauge(g: GaugeConfig) {
  const s = STATUS_STYLES[g.status]
  const pct = (v: number) => Math.max(0, Math.min(100, ((v - g.min) / (g.max - g.min)) * 100))
  const valuePct = g.value !== null ? pct(g.value) : 50
  const tMinPct = pct(g.targetMin)
  const tMaxPct = pct(g.targetMax)

  return (
    <div className="px-5 sm:px-6 py-3.5 border-t border-gray-100 first:border-t-0">
      <div className="flex items-baseline justify-between gap-3 mb-2 flex-wrap">
        <div className="inline-flex items-baseline gap-2">
          <span className="text-[13.5px] font-semibold text-gray-900">{g.name}</span>
          {g.caption && <span className="text-[12px] text-gray-500">{g.caption}</span>}
        </div>
        <div className="inline-flex items-center gap-2">
          <span className="text-[13.5px] font-bold text-gray-900">
            {g.value}<span className="text-gray-500 font-medium ml-0.5">{g.unit}</span>
          </span>
          <StatusPill status={g.status} />
        </div>
      </div>
      {/* Bar */}
      <div className="relative h-2.5">
        <div className="absolute inset-0 rounded-full bg-gray-100" />
        <div
          className="absolute top-0 bottom-0 rounded-full"
          style={{ left: `${tMinPct}%`, width: `${tMaxPct - tMinPct}%`, background: '#dcfce7', border: '1px solid #dcfce7' }}
        />
        <div
          className="absolute -top-[3px] h-4 w-4 rounded-full bg-white shadow-sm"
          style={{ left: `calc(${valuePct}% - 8px)`, border: `3px solid ${s.tone}` }}
        />
      </div>
      {/* Scale labels */}
      <div className="flex justify-between mt-1.5 text-[10.5px] tracking-[0.02em] text-gray-400">
        <span>{g.min}</span>
        <span className="text-green-700">target {g.targetMin}–{g.targetMax}</span>
        <span>{g.max}</span>
      </div>
    </div>
  )
}

function ActionCard({ action, index, lawnSize, soilTestId }: { action: RecommendedAction; index: number; lawnSize: number | null; soilTestId: string }) {
  const level = actionPriorityToLevel(action.priority)
  const isPri = level === 'medium'
  const indexStr = String(index).padStart(2, '0')

  return (
    <article
      className="rounded-2xl overflow-hidden bg-white shadow-elevation-2"
      style={{ border: `1px solid ${isPri ? '#fef3c7' : '#e5e7eb'}` }}
    >
      {/* Header strip */}
      <div
        className="flex items-center gap-3 sm:gap-4 px-4 sm:px-6 py-4 border-b"
        style={{
          background: isPri ? '#fffbeb' : '#f0fdf4',
          borderBottomColor: isPri ? '#fef3c7' : '#dcfce7',
        }}
      >
        <span
          className="inline-flex flex-shrink-0 items-center justify-center rounded-[10px] bg-white h-9 w-9 sm:h-10 sm:w-10 text-[13px] font-bold tracking-[0.04em]"
          style={{
            border: `1px solid ${isPri ? '#fef3c7' : '#dcfce7'}`,
            color: isPri ? '#d97706' : '#15803d',
          }}
        >
          {indexStr}
        </span>
        <div className="flex-1 min-w-0">
          <div className="inline-flex items-center gap-2 mb-1">
            <PriorityPill level={level} />
          </div>
          <h3 className="m-0 text-[16px] sm:text-[19px] font-bold leading-[1.25] tracking-[-0.01em] text-gray-900">
            {action.title}
          </h3>
        </div>
      </div>

      {/* Body */}
      <div className="px-4 sm:px-6 py-5">
        {(() => {
          const { lede, rest } = splitLede(action.explanation)
          return (
            <div className="mb-4">
              <p className="m-0 text-[14px] sm:text-[15px] text-gray-700 leading-[1.6]">{lede || action.explanation}</p>
              {rest && (
                <details
                  className="mt-2 group"
                  onToggle={(e) => {
                    if ((e.currentTarget as HTMLDetailsElement).open) {
                      trackEvent('soil_test_action_expanded', {
                        soil_test_id: soilTestId,
                        action_id: action.id,
                        action_category: action.category,
                        action_priority: action.priority,
                      })
                    }
                  }}
                >
                  <summary className="cursor-pointer list-none text-[12.5px] font-semibold text-green-700 inline-flex items-center gap-1 select-none">
                    <span className="group-open:hidden">Why this matters</span>
                    <span className="hidden group-open:inline">Show less</span>
                    <span className="text-gray-400 group-open:rotate-180 transition-transform">▾</span>
                  </summary>
                  <p className="m-0 mt-2 text-[13.5px] sm:text-[14px] text-gray-600 leading-[1.6]">{rest}</p>
                </details>
              )}
            </div>
          )
        })()}

        {/* Stat row: stacked on mobile, 3-col on sm+ */}
        {(action.recommendation || action.amount || action.timing) && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 mb-4">
            {action.recommendation && <Stat icon={<IconDrop size={13} />} label="Rate" value={action.recommendation} />}
            {action.amount && <Stat icon={<IconBag size={13} />} label={lawnSize ? 'For your lawn' : 'Amount'} value={action.amount} />}
            {action.timing && <Stat icon={<IconCal size={13} />} label="When" value={action.timing} />}
          </div>
        )}

        {/* Product picks — up to 3 catalog matches per action */}
        {action.products && action.products.length > 0 && (
          <div className="rounded-xl border border-green-100 bg-green-50/40 p-3 mb-3">
            <div className="text-[11px] font-bold uppercase tracking-[0.08em] text-green-700 mb-2 px-1">
              {action.products.length === 1 ? 'Recommended product' : 'Recommended products'}
            </div>
            <div className="space-y-2">
              {action.products.slice(0, 3).map((product, idx) => (
                <ProductPickCard
                  key={product.asin ?? product.name}
                  product={product}
                  pickIndex={idx}
                  variant={idx === 0 ? 'primary' : 'alternative'}
                  trackingSlug="tool:soil-test-analyzer"
                  placement="tool-soil-test-analyzer-picks"
                  sourceContext={action.id}
                />
              ))}
            </div>
            <AffiliateDisclosure className="mt-2 px-1" />
          </div>
        )}

        {/* Caveats — inline footer rows, no border */}
        {action.caveats.length > 0 && (
          <ul className="m-0 mb-3 list-none p-0 space-y-1">
            {action.caveats.map((c, i) => (
              <li key={i} className="flex gap-2 text-[12px] sm:text-[12.5px] text-gray-500 leading-[1.5]">
                <span className="flex-shrink-0 mt-0.5 text-gray-400"><IconInfo size={12} /></span>
                <span>{c}</span>
              </li>
            ))}
          </ul>
        )}

        {/* Sources — compressed single line, still fully visible (E-E-A-T) */}
        {action.citations.length > 0 && (
          <div className="text-[11.5px] text-gray-500 flex items-center gap-1.5 flex-wrap">
            <span className="font-semibold text-gray-600">Source{action.citations.length > 1 ? 's' : ''}:</span>
            {action.citations.map((c: Citation, i: number) => (
              <span key={i}>
                <a
                  href={c.url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="text-green-700 font-medium no-underline hover:underline"
                >
                  {c.publisher}
                </a>
                {i < action.citations.length - 1 && <span className="text-gray-300 mx-1">·</span>}
              </span>
            ))}
          </div>
        )}
      </div>
    </article>
  )
}

function Stat({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="rounded-[10px] border border-gray-200 bg-gray-50 px-3 py-2.5">
      <div className="inline-flex items-center gap-1.5 text-[10.5px] sm:text-[11px] font-bold uppercase tracking-[0.06em] text-gray-500">
        <span className="text-green-700">{icon}</span> {label}
      </div>
      <div className="mt-1 text-[13px] sm:text-[13.5px] font-semibold text-gray-900 leading-[1.4]">
        {value}
      </div>
    </div>
  )
}

function ContextCard({ action }: { action: RecommendedAction }) {
  return (
    <article className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5 shadow-elevation-2">
      <div className="flex items-center gap-2.5 mb-2.5 flex-wrap">
        <span className="inline-flex h-[30px] w-[30px] items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-gray-500">
          <IconInfo size={14} />
        </span>
        <h3 className="m-0 text-[14.5px] sm:text-[15px] font-bold tracking-[-0.01em] text-gray-900 flex-1 min-w-0">{action.title}</h3>
        <PriorityPill level="info" />
      </div>
      <p className="m-0 text-[13px] sm:text-[13.5px] text-gray-700 leading-[1.6]">{action.explanation}</p>
      {action.recommendation && (
        <div
          className="mt-3 rounded-[10px] px-3 py-2.5 text-[12.5px] sm:text-[13px] font-medium leading-[1.5] flex gap-2"
          style={{ background: '#f0fdf4', border: '1px solid #dcfce7', color: '#15803d' }}
        >
          <span className="flex-shrink-0 mt-0.5"><IconCheck size={14} /></span>
          <span><strong className="font-bold">What to do:</strong> {action.recommendation}</span>
        </div>
      )}
      {action.citations.length > 0 && (
        <div className="mt-2.5 text-[11.5px] text-gray-500">
          <span className="font-semibold text-gray-600">Source:</span>{' '}
          <a href={action.citations[0].url} target="_blank" rel="noopener noreferrer nofollow" className="text-green-700">
            {action.citations[0].publisher}
          </a>
        </div>
      )}
    </article>
  )
}

interface MicroValue {
  name: string
  value: number | null
  unit: string
}

function MicroBundle({ values, actions }: { values: SoilTestValues; actions: RecommendedAction[] }) {
  const items: MicroValue[] = [
    { name: 'Iron', value: values.ironPpm, unit: 'ppm' },
    { name: 'Manganese', value: values.manganesePpm, unit: 'ppm' },
    { name: 'Zinc', value: values.zincPpm, unit: 'ppm' },
    { name: 'Boron', value: values.boronPpm, unit: 'ppm' },
    { name: 'Copper', value: values.copperPpm, unit: 'ppm' },
    { name: 'Sulfur', value: values.sulfurPpm, unit: 'ppm' },
  ].filter((m) => m.value !== null) as MicroValue[]

  if (items.length === 0) return null

  return (
    <article className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5 shadow-elevation-2">
      <div className="flex items-center gap-2.5 mb-2 flex-wrap">
        <span className="inline-flex h-[30px] w-[30px] items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-gray-500">
          <IconBeaker size={14} />
        </span>
        <h3 className="m-0 text-[14.5px] sm:text-[15px] font-bold tracking-[-0.01em] text-gray-900 flex-1 min-w-0">
          Micronutrients · informational only
        </h3>
        <PriorityPill level="info" />
      </div>
      <p className="m-0 text-[13px] sm:text-[13.5px] text-gray-700 leading-[1.6]">
        Soil tests for micronutrients are useful context but not as reliable as plant tissue tests for deciding whether to supplement.{' '}
        <strong className="font-semibold text-gray-900">This tool treats them as informational only</strong>
        . Talk to your local extension before applying chelated micronutrient products.
      </p>
      <div className="mt-3.5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {items.map((it) => (
          <div key={it.name} className="rounded-[10px] border border-gray-200 bg-gray-50 px-3 py-2.5">
            <div className="text-[10.5px] font-bold uppercase tracking-[0.06em] text-gray-500">{it.name}</div>
            <div className="mt-0.5 text-[14px] font-bold text-gray-900">
              {it.value}<span className="text-gray-500 font-medium text-[11px] ml-0.5">{it.unit}</span>
            </div>
          </div>
        ))}
      </div>
      {actions[0]?.citations[0] && (
        <div className="mt-3 text-[11.5px] text-gray-500">
          <span className="font-semibold text-gray-600">Source:</span>{' '}
          <a href={actions[0].citations[0].url} target="_blank" rel="noopener noreferrer nofollow" className="text-green-700">
            {actions[0].citations[0].publisher}
          </a>
        </div>
      )}
    </article>
  )
}

function AllClearCard({ contextCount }: { contextCount: number }) {
  return (
    <article
      className="rounded-2xl border border-green-100 bg-green-50/40 p-5 sm:p-6 shadow-elevation-2 flex gap-4 items-start"
    >
      <span className="flex-shrink-0">
        <SoilPortraitSVG status="healthy" size={64} />
      </span>
      <div className="flex-1 min-w-0">
        <h2 className="m-0 text-[18px] sm:text-[20px] font-bold tracking-[-0.02em] text-gray-900">
          Maintain what you have
        </h2>
        <p className="m-0 mt-2 text-[13.5px] sm:text-[14px] text-gray-700 leading-[1.6]">
          No urgent amendments needed this year. Stick with your normal fertilization, grasscycle clippings, and re-test in 12 to 18 months to confirm the trend.
          {contextCount > 0 && ' Review the context notes below for nuance.'}
        </p>
      </div>
    </article>
  )
}

function SaveCard({
  saved,
  saving,
  savingError,
  isAuthenticated,
  isPremium,
  onSave,
  onEmailCapture,
}: {
  saved: boolean
  saving: boolean
  savingError: string | null
  isAuthenticated: boolean
  isPremium: boolean
  onSave: () => void
  onEmailCapture?: () => void
}) {
  if (saved) {
    return (
      <div className="rounded-2xl border border-green-100 bg-green-50/60 p-4 sm:p-5">
        <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.08em] text-green-700 mb-2">
          <IconCheck size={13} />
          Saved
        </div>
        <p className="m-0 text-[13.5px] text-gray-700 leading-[1.55]">
          This test is now in your lawn history. We will email a re-test reminder in 12 months.
        </p>
      </div>
    )
  }

  const canSaveDirectly = isAuthenticated && isPremium

  if (canSaveDirectly) {
    return (
      <div className="rounded-2xl border border-green-100 bg-green-50/60 p-4 sm:p-5">
        <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-green-700 mb-2">
          <IconSparkle size={12} />
          Save this result
        </div>
        <h3 className="m-0 text-[15px] sm:text-[16px] font-bold text-gray-900">
          Save to your lawn profile
        </h3>
        <p className="m-0 mt-2 mb-3 text-[12.5px] sm:text-[13px] text-gray-700 leading-[1.55]">
          Adds this result to your lawn history so you can compare against next year&apos;s test.
        </p>
        <button
          type="button"
          disabled={saving}
          onClick={onSave}
          className="inline-flex items-center gap-1.5 rounded-full bg-white px-3.5 py-2 text-[13px] font-bold text-green-700 border border-green-100 hover:bg-green-50 transition-colors disabled:opacity-60"
        >
          {saving ? 'Saving…' : 'Save to lawn'}
          <IconArrow size={12} />
        </button>
        {savingError && <p className="m-0 mt-2 text-[12px] text-red-600">{savingError}</p>}
      </div>
    )
  }

  return (
    <div className="rounded-2xl border border-green-100 bg-green-50/60 p-4 sm:p-5">
      <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-green-700 mb-2">
        <IconSparkle size={12} />
        Keep this report useful
      </div>
      <h3 className="m-0 text-[15px] sm:text-[16px] font-bold text-gray-900">
        Lawn reminders before each window
      </h3>
      <p className="m-0 mt-2 mb-3 text-[12.5px] sm:text-[13px] text-gray-700 leading-[1.55]">
        Get a 12-month re-test nudge and seasonal reminders for the products in your soil report. Free, unsubscribe anytime.
      </p>
      {onEmailCapture && (
        <button
          type="button"
          onClick={onEmailCapture}
          className="inline-flex items-center gap-1.5 rounded-full bg-green-600 px-4 py-2 text-[13px] font-bold text-white hover:bg-green-700 transition-colors"
        >
          Send me lawn reminders
          <IconArrow size={12} />
        </button>
      )}
      <div className="mt-3 pt-3 border-t border-green-100/80">
        <Link
          href="/pricing?source=soil_test"
          onClick={() => trackEvent('upgrade_clicked', { source: 'soil_test_save' })}
          className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-green-700 hover:text-green-800 transition-colors disabled:opacity-60"
        >
          Upgrade for year-over-year tracking
          <IconArrow size={11} />
        </Link>
        <p className="m-0 mt-1 text-[11.5px] text-gray-500 leading-[1.45]">
          Premium ($7.99/mo) keeps every soil test on your lawn profile and lets you compare year over year.
        </p>
      </div>
      {savingError && <p className="m-0 mt-2 text-[12px] text-red-600">{savingError}</p>}
    </div>
  )
}

function TrustNote() {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3.5 text-[12px] text-gray-500 leading-[1.55]">
      <div className="flex items-center gap-2 font-bold text-gray-600 mb-2 whitespace-nowrap">
        <span className="text-green-700"><IconLock size={12} /></span>
        Built on real sources
      </div>
      Recommendations cite university extensions and are sized to your lawn, but lime, sulfur, and potassium rates are estimates unless your lab provides method-specific rates. The original PDF is never stored.
    </div>
  )
}

// ProductPickCard moved to components/tools/shared/product-pick-card.tsx so the
// calculator tools can reuse the same curated-pick card. Imported at the top.

// ============================================================================
// Shopping list — aggregates every product across every action into a single
// deduped list with a grand total. Helps users move from "read the cards" to
// "buy the list" with one decision point instead of N.
// ============================================================================

function ShoppingList({ actionable, soilTestId }: { actionable: RecommendedAction[]; soilTestId: string }) {
  const deduped = useMemo(() => dedupeProducts(actionable), [actionable])
  const total = useMemo(() => sumPrices(deduped), [deduped])

  // Fire a one-shot view event keyed by soilTestId so multi-test sessions
  // count each test once. Hooks must run before any conditional return.
  const viewedRef = useRef<string | null>(null)
  const shouldRender = deduped.length >= 2
  useEffect(() => {
    if (!shouldRender) return
    if (viewedRef.current === soilTestId) return
    viewedRef.current = soilTestId
    trackEvent('soil_test_shopping_list_viewed', {
      soil_test_id: soilTestId,
      product_count: deduped.length,
      total_price: total ?? undefined,
    })
  }, [shouldRender, soilTestId, deduped.length, total])

  // Hide when fewer than 2 unique products — a single product is already
  // shown well inline; no point repeating it as a "list of one."
  if (!shouldRender) return null

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 shadow-elevation-2">
      <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-1 sm:gap-3 mb-3">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-green-700">Your shopping list</div>
          <h2 className="m-0 mt-1 text-[16px] sm:text-[17px] font-bold tracking-[-0.01em] text-gray-900">
            Everything we recommended, in one place
          </h2>
        </div>
        {total !== null && (
          <div className="text-[12.5px] text-gray-600">
            <span className="font-bold text-gray-900">~${total}</span> total · prices approximate
          </div>
        )}
      </div>
      <div className="space-y-2">
        {deduped.map((product, idx) => (
          <ProductPickCard
            key={product.asin ?? product.name}
            product={product}
            pickIndex={idx}
            variant="shopping-list"
            trackingSlug="tool:soil-test-analyzer"
            placement="tool-soil-test-analyzer-picks"
            sourceContext="shopping-list"
          />
        ))}
      </div>
      <AffiliateDisclosure className="mt-3 text-center" />
    </section>
  )
}

// ============================================================================
// Cross-tool nudges
// ============================================================================

// Only tools with verified URL-prefill contracts are listed here. Adding a new
// target requires (a) reading its `page-content.tsx` to confirm the exact
// `searchParams.get(...)` keys it consumes, then (b) building the link from
// those keys — never assume. aeration-calculator and watering-schedule have no
// URL-prefill support today, so they are intentionally omitted.

interface CrossToolCard {
  toolKey: 'fertilizer-calculator' | 'soil-temperature'
  href: string
  title: string
  reason: string
}

function CrossToolNudges({
  actionable,
  grassDisplay,
  lawnSize,
  zipCode,
  soilTestId,
}: {
  actionable: RecommendedAction[]
  grassDisplay: string
  lawnSize: number | null
  zipCode: string | null
  soilTestId: string
}) {
  const cards: CrossToolCard[] = []
  const has = (cat: RecommendedAction['category']) => actionable.some((a) => a.category === cat)
  const hasSulfurAction = actionable.some((a) => a.id === 'ph-high-apply-sulfur')

  if (has('potassium') || has('phosphorus')) {
    const params = new URLSearchParams()
    if (grassDisplay) params.set('grass', grassDisplay)
    if (lawnSize) params.set('size', String(lawnSize))
    const qs = params.toString()
    cards.push({
      toolKey: 'fertilizer-calculator',
      href: `/tools/fertilizer-calculator${qs ? `?${qs}` : ''}`,
      title: 'Plan your fertilizer pass',
      reason: 'Take the K and P from this report into a full feeding plan for your grass and lawn size.',
    })
  }

  if (hasSulfurAction && zipCode) {
    const params = new URLSearchParams({ location: zipCode })
    cards.push({
      toolKey: 'soil-temperature',
      href: `/tools/soil-temperature?location=${encodeURIComponent(zipCode)}`,
      title: 'Time your sulfur application',
      reason: 'Sulfur conversion stalls below 55°F. Check when your soil is warm enough to apply.',
    })
  }

  if (cards.length === 0) return null

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 shadow-elevation-2">
      <div className="mb-3">
        <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-green-700">Use these results in</div>
        <h2 className="m-0 mt-1 text-[16px] sm:text-[17px] font-bold tracking-[-0.01em] text-gray-900">
          The next tool, pre-filled
        </h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {cards.map((c) => (
          <a
            key={c.toolKey}
            href={c.href}
            onClick={() => {
              trackEvent('soil_test_cross_tool_click', {
                soil_test_id: soilTestId,
                target_tool: c.toolKey,
                source_action_categories: actionable.map((a) => a.category).join(','),
              })
            }}
            className="group rounded-xl border border-gray-200 bg-gray-50/60 hover:bg-green-50/40 hover:border-green-100 transition-colors p-3.5 no-underline"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="text-[13.5px] sm:text-[14px] font-bold text-gray-900 leading-[1.3]">
                {c.title}
              </div>
              <span className="flex-shrink-0 mt-0.5 text-green-700 group-hover:translate-x-0.5 transition-transform">
                <IconArrow size={12} />
              </span>
            </div>
            <p className="m-0 mt-1.5 text-[12.5px] text-gray-600 leading-[1.5]">{c.reason}</p>
          </a>
        ))}
      </div>
    </section>
  )
}

// ============================================================================
// Misc
// ============================================================================

function labLabel(lab: SoilTestValues['lab']): string {
  switch (lab) {
    case 'logan_labs': return 'Logan Labs'
    case 'waypoint': return 'Waypoint Analytical'
    case 'spectrum': return 'Spectrum Analytic'
    case 'al_eastern': return 'A&L Eastern'
    case 'umass': return 'UMass Extension'
    case 'manual': return 'Manual entry'
    default: return 'Soil test report'
  }
}
